import React, { useState, useEffect } from 'react'
import moment from 'moment'
import {
    Document,
    Page,
    View,
    Text,
    StyleSheet,
    Image,
    Font,
    Svg,
    Path,
} from '@react-pdf/renderer'
import PropTypes, { object } from 'prop-types'

import logo from 'images/logos/primary_white.png'
import EventStatsPDFOverview from './Overview'
import EventStatsPDFDetails from './Details'
import EventSeriesData from './EventSeriesData'
import { formatEventSubheadlineLocation } from 'utils/helpers'
import { useTranslation } from 'react-i18next'

const thinFontSrc = 'src/fonts/TTCommons/TTCommons-Thin.ttf'
const regularFontSrc = 'src/fonts/TTCommons/TTCommons-Regular.ttf'
const boldFontSrc = 'src/fonts/TTCommons/TTCommons-DemiBold.ttf'

Font.register({
    family: 'TTCommons',
    fonts: [
        { src: thinFontSrc }, // font-style: normal, font-weight: normal
        { src: regularFontSrc, fontWeight: 500 },
        { src: boldFontSrc, fontWeight: 700 },
    ],
})

const pdfStyles = StyleSheet.create({
    page: {
        padding: '0',
    },

    logo: {
        height: 35,
        width: 110,
    },

    section: {
        padding: '16px 32px',
    },

    sectionHeadline: {
        fontSize: '14px',
        textTransform: 'capitalize',
    },

    addressLine: {
        marginTop: '6px',
        display: 'flex',
        flexDirection: 'row',
    },

    addressLineValues: {
        color: '#818181',
        fontSize: '10px',
        marginRight: '12px',
        display: 'flex',
        flexDirection: 'row',
    },

    svg: {
        marginRight: '4px'
    },

    header: {
        backgroundColor: '#031C41',
        color: '#fff',
        padding: '16px 32px',
    },

    footer: {
        backgroundColor: '#031C41',
        color: '#fff',
        padding: '16px 32px',
        alignSelf: 'flex-end',
        marginTop: 'auto',
        width: '100%',
        fontSize: '8px',
    },

    footerText: {
        marginTop: '4px',
    },
})
const EventStatsPDF = ({ fbInsights, eventStats, eventSeriesStats, eventSeries, eventInfo, numOfTCs, adCreatives = {} }) => {
    const [userCurrency, setUserCurrency] = useState('EUR')
    const { t } = useTranslation()

    useEffect(() => {
        const currency = localStorage.getItem('userCurrency')
        if (currency) {
            setUserCurrency(currency)
        }
    }, [])
    
    const currency = userCurrency || 'CHF'

    return (
        <Document>
            <Page size="A4" style={pdfStyles.page}>
                <View style={pdfStyles.header}>
                    <Image src={logo} style={pdfStyles.logo} />
                </View>

                <View style={pdfStyles.section}>
                    <Text style={pdfStyles.sectionHeadline}>
                        {eventInfo.title.toLowerCase()}
                    </Text>
                    <View style={pdfStyles.addressLine}>
                        <View style={pdfStyles.addressLineValues}>
                            <Svg viewBox="0 0 100 100"  width={10} height={10} fill="none" xmlns="http://www.w3.org/2000/svg"style={pdfStyles.svg}>
                                <Path fill-rule="evenodd" clip-rule="evenodd" d="M87.5015 50C87.5015 29.3742 70.6258 12.4985 50 12.4985C29.3742 12.4985 12.4985 29.3742 12.4985 50C12.4985 70.6258 29.3742 87.5015 50 87.5015C70.6258 87.5015 87.5015 70.6258 87.5015 50ZM0 50C0 22.5009 22.5009 0 50 0C77.4991 0 100 22.5009 100 50C100 77.4991 77.4991 100 50 100C22.5009 100 0 77.4991 0 50ZM68.1239 67.1849C68.7509 67.5013 69.6885 67.8119 70.626 67.8119C72.1904 67.8119 73.7493 66.8744 74.6927 65.6263C75.9407 63.4407 75.3137 60.3116 73.1281 59.0635L54.6879 47.5026V32.5002C54.6879 29.9981 52.5023 27.8125 50.0002 27.8125C47.4981 27.8125 45.3125 29.9981 45.3125 32.5002V52.8113L68.1239 67.1849Z" fill="#818181"/>
                            </Svg>
                            <Text>
                                {moment.utc(eventInfo.date).format('DD.MM.YYYY HH:mm')}
                            </Text>
                        </View>
                        <View style={pdfStyles.addressLineValues}>
                            <Svg viewBox="0 0 100 140"  width={10} height={10} fill="none" xmlns="http://www.w3.org/2000/svg" style={pdfStyles.svg}>
                                <Path d="M49.9999 0C22.2773 0 0 21.4342 0 48.1269C0 74.811 49.9999 140 49.9999 140C49.9999 140 100 74.811 100 48.1269C100 21.4342 77.7227 0 49.9999 0ZM49.9999 69.9958C37.2764 69.9958 27.2712 60.3738 27.2712 48.1185C27.2712 35.8715 37.2676 26.2411 49.9999 26.2411C62.7236 26.2411 72.7288 35.8631 72.7288 48.1185C72.7288 60.3736 62.7238 69.9958 49.9999 69.9958Z" fill="#818181"/>
                            </Svg>
                            <Text>
                                {formatEventSubheadlineLocation({
                                    city: eventInfo.city
                                })}
                            </Text>
                        </View>

                        <View style={pdfStyles.addressLineValues}>
                            <Svg viewBox="0 0 314 228"  width={14} height={10} fill="none" xmlns="http://www.w3.org/2000/svg" style={pdfStyles.svg}>
                                <Path fill-rule="evenodd" clip-rule="evenodd" d="M263.16 32.0255C266.076 30.4414 268.258 29.9919 270.125 30.0001C272.088 30.0087 274.718 30.5318 278.463 32.3452C279.865 33.0242 280.678 33.7396 281.31 34.6178C282 35.5769 282.967 37.4302 283.621 41.0292C283.582 40.8141 283.587 40.9218 283.615 41.4908L283.615 41.4924C283.65 42.2186 283.72 43.6941 283.781 46.2053C283.873 50.0065 283.933 55.17 283.966 61.4124C284.031 73.5486 283.996 89.4938 283.916 106.845H243C234.992 106.845 228.5 113.336 228.5 121.345V197.845H201V158.124C201 149.011 193.613 141.624 184.5 141.624H122.5H59C49.8873 141.624 42.5 149.011 42.5 158.124V197.845H30V106.494C30 106.498 30.0067 106.114 30.108 105.436C30.2047 104.789 30.363 104.041 30.5934 103.286C31.1006 101.624 31.7009 100.744 31.9934 100.443C35.1372 97.2059 36.5017 96.0363 37.4053 95.4464C37.7972 95.1905 38.1558 94.9614 39.5659 94.7688C39.552 94.7707 39.5446 94.7717 39.5446 94.7718C39.5446 94.7719 39.5522 94.7712 39.5682 94.7697C39.64 94.7627 39.8816 94.7393 40.3695 94.705C41.0011 94.6606 41.8272 94.6123 42.8847 94.5563C44.0018 94.4972 45.1092 94.4427 46.3532 94.3815L46.3558 94.3814C47.7166 94.3144 49.2409 94.2394 51.1203 94.1415C57.8212 93.7925 66.7139 93.2647 76.968 92.3226C97.3881 90.4466 123.579 86.8973 149.04 79.6672C198.823 65.5304 220.3 55.3065 263.16 32.0255ZM67.5 197.845H110V166.624H67.5V197.845ZM135 197.845H176V166.624H135V197.845ZM253.5 197.845H283.41C283.46 184.073 283.562 167.661 283.667 150.699L283.667 150.632C283.706 144.409 283.745 138.112 283.782 131.845H253.5V197.845ZM291.537 5.34405C284.868 2.1149 277.794 0.0334597 270.257 0.000400543C262.625 -0.0330763 255.514 2.03869 248.84 5.66367C207.309 28.2231 187.919 37.4407 140.845 50.8083C103.848 61.314 66.5269 63.2489 47.1498 64.2535H47.1497C41.7337 64.5343 37.7196 64.7424 35.5063 65.0447C30.2034 65.769 25.5274 67.3734 21.0045 70.3264C16.9933 72.9453 13.5629 76.3599 10.4734 79.5407C5.77912 84.3738 3.26274 90.0638 1.89994 94.5292C0.533147 99.0077 0 103.325 0 106.494V209.845C0 219.786 8.05896 227.845 18 227.845H296.379C305.315 227.845 313.373 220.727 313.38 210.9C313.391 195.23 313.525 173.636 313.666 150.973C313.754 136.808 313.845 122.224 313.909 108.409C313.993 90.4946 314.034 73.8796 313.966 61.2513C313.932 54.9459 313.871 49.5631 313.772 45.4813C313.69 42.0821 313.571 38.0443 313.137 35.66C310.471 21.0041 303.079 10.9327 291.537 5.34405Z" fill="#818181"/>
                            </Svg>
                            <Text>
                                {formatEventSubheadlineLocation({
                                    hall: eventInfo.hall_name,
                                    venue: eventInfo.venue_name
                                })}
                            </Text>
                        </View>
                    </View>
                    <View style={pdfStyles.addressLine}>
                        <Text style={pdfStyles.addressLineValues}>
                            {t('Event.statistics.eventStatsPDF.createdAt')}: {moment().format('DD.MM.YYYY HH:mm')}
                        </Text>
                    </View>
                </View>

                <EventStatsPDFOverview fbInsights={fbInsights} currency={currency} adCreatives={adCreatives} numOfTCs={numOfTCs} />
                <EventStatsPDFDetails eventStats={eventStats} eventSeriesStats={eventSeriesStats} currency={currency} />
                {eventSeries.length ? <EventSeriesData eventSeries={eventSeries} /> : null}

                {/* FOOTER  */}
                <View style={pdfStyles.footer}>
                    <Text>
                        {t('Event.statistics.eventStatsPDF.companyDetails.1')}
                    </Text>
                    <Text style={pdfStyles.addressLine}></Text>
                    <Text style={pdfStyles.footerText}>
                        {t('Event.statistics.eventStatsPDF.companyDetails.2')}
                    </Text>
                </View>
            </Page>
        </Document>
    )
}

EventStatsPDF.propTypes = {
    fbInsights: PropTypes.object,
    eventStats: PropTypes.object,
    eventSeriesStats: PropTypes.object,
    eventSeries: PropTypes.arrayOf(object),
    eventInfo: PropTypes.object,
    numOfTCs: PropTypes.number,
    adCreatives: PropTypes.object
}

export default EventStatsPDF
